// apps/spa/src/components/reminders/LiveCountdown.tsx
// Renders the "Next run" cell value for a reminder row.
//
// Consumes useCountdownTick(60_000) to trigger a re-render every 60 seconds
// while the tab is visible. On each tick, recomputes the countdown from the
// current Date.now() — no per-row timers, one global tick.
//
// Cases:
//   triggerType='event' + eventMatchMode='all'      → 'On every event'
//   triggerType='event' + eventMatchMode='specific' → 'Event: <name> <relative>'
//     - event found in list: show time until event minus lead time
//     - event not found / no events: '—' (orphaned binding)
//   scheduleCron non-null + enabled                 → formatCountdownMs of next run
//   scheduleCron null OR !enabled                   → '—'

import { useReducedMotion, motion, AnimatePresence } from 'motion/react';
import type { Reminder } from '@guardian/shared';
import type { ChannelOption } from '@guardian/shared';
import type { GuildScheduledEvent } from '@/hooks/useEvents';
import { useCountdownTick } from '@/hooks/useCountdownTick';
import { nextRunFromCron, formatCountdownMs } from '@/lib/schedule';

export interface LiveCountdownProps {
  reminder: Reminder;
  events?: GuildScheduledEvent[];
  // channels not needed for countdown itself (used by ReminderHistoryPanel)
}

export function LiveCountdown({ reminder, events = [] }: LiveCountdownProps) {
  // Subscribe to 60-second tick — this causes re-render every minute while visible
  const tick = useCountdownTick(60_000);
  const reduce = useReducedMotion();

  const { triggerType, eventMatchMode, eventId, eventLeadMinutes, scheduleCron, enabled } = reminder;

  // Wrap the displayed text in a motion span that pulses briefly on each tick.
  // The `key` prop forces AnimatePresence to treat each tick as a new element,
  // giving a quick fade+scale that signals the value just refreshed.
  function TickSpan({ children }: { children: React.ReactNode }) {
    if (reduce) return <span>{children}</span>;
    return (
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={tick}
          initial={{ opacity: 0.6, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          {children}
        </motion.span>
      </AnimatePresence>
    );
  }

  // Event-triggered reminders
  if (triggerType === 'event') {
    if (eventMatchMode === 'all') {
      return <span>On every event</span>;
    }
    if (eventMatchMode === 'specific') {
      const event = events.find((e) => e.id === eventId);
      if (!event) {
        // Orphaned binding or events list not yet loaded
        return <span>—</span>;
      }
      const fireAt = event.scheduledStartTime - (eventLeadMinutes ?? 0) * 60_000;
      const ms = fireAt - Date.now();
      return <TickSpan>Event: {event.name} {formatCountdownMs(ms)}</TickSpan>;
    }
    return <span>—</span>;
  }

  // Cron-triggered reminders
  if (!enabled || scheduleCron === null) {
    return <span>—</span>;
  }

  const next = nextRunFromCron(scheduleCron);
  if (next === null) return <span>—</span>;

  const ms = next.getTime() - Date.now();
  return <TickSpan>{formatCountdownMs(ms)}</TickSpan>;
}

// Re-export ChannelOption type for the parent (ReminderTable) convenience
export type { ChannelOption };
